import type {
  GoogleAutoCampaignRow,
  GoogleAutoChartFamily,
  GoogleAutoCounts,
  GoogleAutoGroup,
  GoogleAutoMoney,
  PortalGoogleAutoHeadline,
  PortalGoogleAutoPanel,
  PortalGoogleAutomotiveReport,
} from '../../types/portalAutomotive.js';
import { GOOGLE_AUTO_GROUPS, isGoogleAutoGroup } from '../../types/portalAutomotive.js';
import type { PortalCoverage, PortalGrouping } from '../../types/portalReport.js';
import { safeCampaignName } from './automotiveReport.js';
import { buildSeries, type MeasuredBucket } from './series.js';
import { yearOnYearMonths } from './yearOnYear.js';

// ⚠️⚠️ THE AUTOMOTIVE GOOGLE REPORT (2026-11-12) — the pure core. PURE: no I/O.
//
// The Google twin of automotiveReport.ts. WHICH CAMPAIGN IS IN WHICH GROUP is
// decided in server/modules/googleAdsReporting/query/campaignGroup.ts and nowhere
// else; this file decides what each group SHOWS and checks the groups against an
// independently queried headline.
//
// ⚠️ Because a campaign is in exactly one group, spend and every count sum across
// the four groups to the headline BY CONSTRUCTION, and `reconcileGoogleAuto` checks
// that against the witness.
//
// ⚠️ CONVERSIONS ARE FRACTIONAL (data-driven attribution) and stay fractional on the
// wire; the page shows one decimal. Never rounded to a whole number here.
//
// ⚠️ NOTHING HERE NAMES A BRAND OR AN ACCOUNT. Who gets this report is
// `resolveReportTemplate`.

export interface GoogleAutoPanelSpec {
  group: GoogleAutoGroup;
  title: string;
  /**
   * Every group renders only when it has something in the SELECTED OR the
   * COMPARISON period. There is no always-on group: a dealer running only VLA must
   * not be shown a Search section that teaches them to ignore sections.
   */
  render: 'when_active';
}

export const GOOGLE_AUTO_PANELS: readonly GoogleAutoPanelSpec[] = [
  { group: 'vla', title: 'Vehicle listing ads (VLA)', render: 'when_active' },
  { group: 'search', title: 'Search campaigns', render: 'when_active' },
  { group: 'pmax', title: 'Performance Max campaigns', render: 'when_active' },
  { group: 'other', title: 'Other campaigns', render: 'when_active' },
];

export function googleAutoPanelSpecFor(group: GoogleAutoGroup): GoogleAutoPanelSpec {
  const spec = GOOGLE_AUTO_PANELS.find((p) => p.group === group);
  if (!spec) throw new Error(`automotiveGoogle: no panel spec for ${group}`);
  return spec;
}

/** Said when no group has anything — one sentence, not four empty sections. */
export const GOOGLE_AUTO_NONE_SENTENCE = 'No Google campaigns ran in this period.';

// ---------------------------------------------------------------------------
// Rows in
// ---------------------------------------------------------------------------

export interface GoogleAutoBucketRow {
  group: GoogleAutoGroup;
  currency: string;
  spend: number;
  conversions: number;
  impressions: number;
  clicks: number;
}

export interface GoogleAutoTotalsRow {
  currency: string;
  spend: number;
  conversions: number;
  impressions: number;
  clicks: number;
}

export interface GoogleAutoSeriesRow extends GoogleAutoBucketRow {
  date: string;
}

export interface GoogleAutoCampaignInput {
  campaignId: string;
  name: string | null;
  group: GoogleAutoGroup;
  currency: string;
  spend: number;
  conversions: number;
  impressions: number;
  clicks: number;
}

/** A group off the wire. ⚠️ Unknown lands in `other` rather than being dropped. */
export function googleAutoGroupOrOther(v: unknown): GoogleAutoGroup {
  return isGoogleAutoGroup(v) ? v : 'other';
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Conversions are fractional; kept to two decimals on the wire so sums stay stable. */
const roundConv = (n: number): number => Math.round(n * 100) / 100;

export function emptyGoogleAutoCounts(): GoogleAutoCounts {
  return { conversions: 0, clicks: 0, impressions: 0 };
}

/** Cost per X — null for X = 0 (never a cost of nothing, never infinity). */
export function googleCostPerOrNull(spend: number, count: number): number | null {
  return count > 0 ? round2(spend / count) : null;
}

/** Click-through rate as a percentage; null when nothing was served. */
export function googleCtrOrNull(counts: Pick<GoogleAutoCounts, 'clicks' | 'impressions'>): number | null {
  return counts.impressions > 0 ? (counts.clicks / counts.impressions) * 100 : null;
}

type CountSource = Pick<GoogleAutoBucketRow, 'conversions' | 'clicks' | 'impressions'>;

function sumCounts(rows: readonly CountSource[]): GoogleAutoCounts {
  const c = emptyGoogleAutoCounts();
  for (const r of rows) {
    c.conversions += r.conversions;
    c.clicks += r.clicks;
    c.impressions += r.impressions;
  }
  c.conversions = roundConv(c.conversions);
  return c;
}

export function googleAutoHasActivity(counts: GoogleAutoCounts, spend: number): boolean {
  return spend > 0 || counts.conversions > 0 || counts.clicks > 0 || counts.impressions > 0;
}

function moneyFor(rows: readonly GoogleAutoBucketRow[]): GoogleAutoMoney[] {
  // ⚠️ MONEY IS PER CURRENCY: each cost-per divides a currency's spend by THAT
  // currency's own count — never a blended figure.
  const byCurrency = new Map<string, GoogleAutoBucketRow>();
  for (const r of rows) {
    const cur = byCurrency.get(r.currency);
    byCurrency.set(
      r.currency,
      cur
        ? {
            ...cur,
            spend: cur.spend + r.spend,
            conversions: cur.conversions + r.conversions,
            clicks: cur.clicks + r.clicks,
            impressions: cur.impressions + r.impressions,
          }
        : { ...r },
    );
  }
  return [...byCurrency.values()]
    .sort((a, b) => b.spend - a.spend)
    .map((c) => ({
      currency: c.currency,
      spend: round2(c.spend),
      costPerConversion: googleCostPerOrNull(c.spend, c.conversions),
      costPerClick: googleCostPerOrNull(c.spend, c.clicks),
    }));
}

function buildCampaignRows(rows: readonly GoogleAutoCampaignInput[]): GoogleAutoCampaignRow[] {
  return rows
    .map((c) => ({
      campaignId: c.campaignId,
      // ⚠️ Through the PII guard: a campaign can be named by a person typing anything.
      name: safeCampaignName(c.name),
      currency: c.currency,
      spend: round2(c.spend),
      conversions: roundConv(c.conversions),
      clicks: c.clicks,
      impressions: c.impressions,
    }))
    .sort((a, b) => b.spend - a.spend);
}

export interface GoogleAutoSeriesOptions {
  range: { from: string; to: string };
  grouping: PortalGrouping;
  today: string;
  coverage: PortalCoverage;
}

function monthsWindowFrom(today: string): string {
  return `${yearOnYearMonths(today)[0] as string}-01`;
}

/** The date range the series query must cover: the period ∪ twelve months. */
export function googleAutoSeriesRange(
  range: { from: string; to: string },
  today: string,
): { from: string; to: string } {
  const monthsFrom = monthsWindowFrom(today);
  return {
    from: range.from < monthsFrom ? range.from : monthsFrom,
    to: range.to > today ? range.to : today,
  };
}

/**
 * One chart family from rows. ⚠️ BUILT ON THE SHARED SERIES CORE, so the gap /
 * partial-month / never-a-zero rules are the report's, not a copy of them. The
 * count rides in `leads` (conversions) and the cost-per in `costPerLead`.
 *
 * ⚠️ Rows spanning MORE THAN ONE CURRENCY carry no money at all — spend and
 * cost-per nulled, `currency: null` — rather than a blended line. Clicks and
 * conversions are counts and survive.
 */
export function buildGoogleAutoFamily(
  key: string,
  rows: readonly GoogleAutoSeriesRow[],
  opts: GoogleAutoSeriesOptions,
): GoogleAutoChartFamily {
  const monthsFrom = monthsWindowFrom(opts.today);
  const currencies = new Set(rows.map((r) => r.currency));
  const withMoney = currencies.size === 1;
  const measured: MeasuredBucket[] = rows.map((r) => ({
    bucket: r.date,
    leads: r.conversions,
    spend: withMoney ? r.spend : 0,
    impressions: r.impressions,
    clicks: r.clicks,
  }));
  const within = (from: string, to: string) => measured.filter((m) => m.bucket >= from && m.bucket <= to);
  const shape = (points: ReturnType<typeof buildSeries>) =>
    points.map((p) => (withMoney ? p : { ...p, spend: null, costPerLead: null }));
  return {
    key,
    currency: withMoney ? ([...currencies][0] ?? null) : null,
    period: shape(
      buildSeries({
        from: opts.range.from,
        to: opts.range.to,
        grouping: opts.grouping,
        measured: within(opts.range.from, opts.range.to),
        coverage: opts.coverage,
        today: opts.today,
      }),
    ),
    months: shape(
      buildSeries({
        from: monthsFrom,
        to: opts.today,
        grouping: 'monthly',
        measured: within(monthsFrom, opts.today),
        coverage: opts.coverage,
        today: opts.today,
      }),
    ),
  };
}

export function buildGoogleAutoPanels(
  rows: readonly GoogleAutoBucketRow[],
  comparisonRows: readonly GoogleAutoBucketRow[] | null,
  campaigns: readonly GoogleAutoCampaignInput[],
  seriesRows: readonly GoogleAutoSeriesRow[],
  opts: GoogleAutoSeriesOptions,
): PortalGoogleAutoPanel[] {
  return GOOGLE_AUTO_GROUPS.map((group) => {
    const mine = rows.filter((r) => r.group === group);
    const theirs = (comparisonRows ?? []).filter((r) => r.group === group);
    return {
      group,
      counts: sumCounts(mine),
      money: moneyFor(mine),
      campaigns: buildCampaignRows(campaigns.filter((c) => c.group === group)),
      chart: buildGoogleAutoFamily(
        `google-${group}`,
        seriesRows.filter((r) => r.group === group),
        opts,
      ),
      activeInComparison: googleAutoHasActivity(
        sumCounts(theirs),
        theirs.reduce((a, r) => a + r.spend, 0),
      ),
    };
  });
}

export function buildGoogleAutoHeadline(totals: readonly GoogleAutoTotalsRow[]): PortalGoogleAutoHeadline {
  const c = sumCounts(totals);
  const spend = new Map<string, number>();
  for (const t of totals) spend.set(t.currency, (spend.get(t.currency) ?? 0) + t.spend);
  return {
    conversions: c.conversions,
    clicks: c.clicks,
    impressions: c.impressions,
    spend: [...spend.entries()]
      .map(([currency, s]) => ({ currency, spend: round2(s) }))
      .sort((a, b) => b.spend - a.spend),
  };
}

/** Whether a group is drawn: something in the selected OR the comparison period. */
export function googleAutoPanelVisible(panel: PortalGoogleAutoPanel): boolean {
  const spend = panel.money.reduce((a, m) => a + m.spend, 0);
  return googleAutoHasActivity(panel.counts, spend) || panel.activeInComparison;
}

/** True when NO group is drawn — the page says one sentence instead. */
export function googleAutoNothingRan(report: PortalGoogleAutomotiveReport): boolean {
  return !report.panels.some(googleAutoPanelVisible);
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE RECONCILIATION — checked against an INDEPENDENTLY queried headline.
// ---------------------------------------------------------------------------

export interface GoogleAutoReconcileMismatch {
  measure: 'conversions' | 'clicks' | 'impressions' | 'spend';
  currency: string | null;
  panels: number;
  headline: number;
}

const SPEND_TOLERANCE = 0.04; // four groups, each rounded to the cent
const CONV_TOLERANCE = 0.05; // each group rounded to 2dp

export function reconcileGoogleAuto(
  panels: readonly PortalGoogleAutoPanel[],
  headline: PortalGoogleAutoHeadline,
): GoogleAutoReconcileMismatch[] {
  const out: GoogleAutoReconcileMismatch[] = [];
  const sum = (k: keyof GoogleAutoCounts): number => panels.reduce((a, p) => a + p.counts[k], 0);
  const checks: Array<['conversions' | 'clicks' | 'impressions', number, number]> = [
    ['conversions', sum('conversions'), headline.conversions],
    ['clicks', sum('clicks'), headline.clicks],
    ['impressions', sum('impressions'), headline.impressions],
  ];
  for (const [k, p, h] of checks) {
    const tol = k === 'conversions' ? CONV_TOLERANCE : 1e-6;
    if (Math.abs(p - h) > tol) out.push({ measure: k, currency: null, panels: p, headline: h });
  }
  const panelSpend = new Map<string, number>();
  for (const p of panels) {
    for (const m of p.money) panelSpend.set(m.currency, (panelSpend.get(m.currency) ?? 0) + m.spend);
  }
  const currencies = new Set([...panelSpend.keys(), ...headline.spend.map((s) => s.currency)]);
  for (const c of currencies) {
    const p = panelSpend.get(c) ?? 0;
    const h = headline.spend.find((s) => s.currency === c)?.spend ?? 0;
    if (Math.abs(p - h) > SPEND_TOLERANCE) out.push({ measure: 'spend', currency: c, panels: p, headline: h });
  }
  return out;
}

export function buildGoogleAutomotiveReport(input: {
  rows: readonly GoogleAutoBucketRow[];
  comparisonRows: readonly GoogleAutoBucketRow[] | null;
  campaigns: readonly GoogleAutoCampaignInput[];
  seriesRows: readonly GoogleAutoSeriesRow[];
  totals: readonly GoogleAutoTotalsRow[];
  opts: GoogleAutoSeriesOptions;
}): { report: PortalGoogleAutomotiveReport; mismatches: GoogleAutoReconcileMismatch[] } {
  const panels = buildGoogleAutoPanels(
    input.rows,
    input.comparisonRows,
    input.campaigns,
    input.seriesRows,
    input.opts,
  );
  const headline = buildGoogleAutoHeadline(input.totals);
  const mismatches = reconcileGoogleAuto(panels, headline);
  return {
    report: {
      headline,
      panels,
      reconciles: mismatches.length === 0,
      hasComparison: input.comparisonRows !== null,
      chart: buildGoogleAutoFamily('google-all', input.seriesRows, input.opts),
    },
    mismatches,
  };
}
